/**
 * Product Mobile Image Slider
 * 
 * Handles the mobile slider functionality for product images on collection pages
 */

class ProductMobileSlider {
  constructor() {
    this.init();
    this.setupMutationObserver();
  }

  init() {
    // Only run on mobile devices
    if (window.innerWidth > 767) return;
    
    // Find all mobile sliders
    const sliders = document.querySelectorAll('.product-loop__mobile-slider');
    
    sliders.forEach(slider => {
      this.setupSlider(slider);
    });
  }

  setupSlider(slider) {
    const dots = slider.querySelectorAll('.slider-dot');
    const images = slider.querySelectorAll('.slider-image');
    const sliderContainer = slider.querySelector('.slider-images');
    
    // Set up the slider for horizontal sliding
    if (images.length > 1) {
      // Position images side by side
      images.forEach((image, index) => {
        image.style.position = 'absolute';
        image.style.left = `${index * 100}%`;
        image.style.transform = 'translateX(0)';
        image.style.transition = 'transform 0.3s ease';
        image.style.opacity = '1';
      });
      
      // Set the active slide
      this.setActiveSlide(slider, 0);
    } else if (images.length === 1) {
      // If only one image, make it visible
      images[0].classList.add('active');
    }
    
    // Add click event to dots
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.showSlide(slider, index);
      });
    });

    // Add touch events for swipe
    if (sliderContainer) {
      let touchStartX = 0;
      let touchStartY = 0;
      let touchEndX = 0;
      let touchEndY = 0;
      let isSwiping = false;
      
      sliderContainer.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
        touchStartY = e.changedTouches[0].screenY;
        isSwiping = true;
      }, { passive: true });
      
      sliderContainer.addEventListener('touchmove', (e) => {
        if (!isSwiping) return;
        
        const currentX = e.changedTouches[0].screenX;
        const currentY = e.changedTouches[0].screenY;
        
        // Calculate the distance moved in both directions
        const deltaX = Math.abs(currentX - touchStartX);
        const deltaY = Math.abs(currentY - touchStartY);
        
        // If the vertical movement is greater than the horizontal movement,
        // cancel the swipe and let the page scroll
        if (deltaY > deltaX) {
          isSwiping = false;
        }
      }, { passive: true });
      
      sliderContainer.addEventListener('touchend', (e) => {
        if (!isSwiping) return;
        
        touchEndX = e.changedTouches[0].screenX;
        touchEndY = e.changedTouches[0].screenY;
        
        // Calculate the distance moved in both directions
        const deltaX = Math.abs(touchEndX - touchStartX);
        const deltaY = Math.abs(touchEndY - touchStartY);
        
        // Only handle horizontal swipes
        if (deltaX > deltaY) {
          this.handleSwipe(slider, touchStartX, touchEndX);
        }
        
        isSwiping = false;
      }, { passive: true });
    }
  }

  setActiveSlide(slider, index) {
    const dots = slider.querySelectorAll('.slider-dot');
    const images = slider.querySelectorAll('.slider-image');
    
    // Update active dot
    dots.forEach(dot => dot.classList.remove('active'));
    if (dots[index]) {
      dots[index].classList.add('active');
    }
    
    // Move all images to create sliding effect
    images.forEach(image => {
      image.style.transform = `translateX(-${index * 100}%)`;
    });
  }

  showSlide(slider, index) {
    this.setActiveSlide(slider, index);
  }

  handleSwipe(slider, touchStartX, touchEndX) {
    const dots = slider.querySelectorAll('.slider-dot');
    const images = slider.querySelectorAll('.slider-image');
    
    if (dots.length <= 1 || images.length <= 1) return;
    
    // Find current active index
    let currentIndex = 0;
    dots.forEach((dot, index) => {
      if (dot.classList.contains('active')) {
        currentIndex = index;
      }
    });
    
    // Determine swipe direction
    const swipeThreshold = 50;
    const diff = touchStartX - touchEndX;
    
    if (Math.abs(diff) < swipeThreshold) return;
    
    let newIndex;
    if (diff > 0) {
      // Swipe left - go to next slide
      newIndex = currentIndex === dots.length - 1 ? 0 : currentIndex + 1;
    } else {
      // Swipe right - go to previous slide
      newIndex = currentIndex === 0 ? dots.length - 1 : currentIndex - 1;
    }
    
    this.showSlide(slider, newIndex);
  }

  setupMutationObserver() {
    // Create a mutation observer to watch for dynamically added products
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              const sliders = node.querySelectorAll ? node.querySelectorAll('.product-loop__mobile-slider') : [];
              if (sliders.length > 0) {
                sliders.forEach(slider => {
                  // Only initialize new sliders
                  if (!slider.dataset.initialized) {
                    this.setupSlider(slider);
                    slider.dataset.initialized = 'true';
                  }
                });
              }
              
              if (node.classList && node.classList.contains('product-loop__mobile-slider')) {
                if (!node.dataset.initialized) {
                  this.setupSlider(node);
                  node.dataset.initialized = 'true';
                }
              }
            }
          });
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  new ProductMobileSlider();
});

// Initialize on window resize
let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    // Only reinitialize if the device type has changed
    const wasMobile = window.innerWidth <= 767;
    const isMobile = window.innerWidth <= 767;
    if (wasMobile !== isMobile) {
      new ProductMobileSlider();
    }
  }, 250);
});

// Initialize when the page is loaded through AJAX
document.addEventListener('shopify:section:load', () => {
  new ProductMobileSlider();
});

// Additional event listeners for Shopify's filtering system
document.addEventListener('shopify:section:unload', () => {
  setTimeout(() => {
    new ProductMobileSlider();
  }, 100);
});

document.addEventListener('shopify:section:select', () => {
  new ProductMobileSlider();
});

// For collection filtering
document.addEventListener('collection:updated', () => {
  // This is a custom event that might be triggered by the theme when collection is filtered
  new ProductMobileSlider();
});